<template>
  <div @keydown.enter.stop>
    <VSelect
      ref="selectEl"
      v-model="selectedTime"
      :options="displayOptions"
      :clearable="false"
      :searchable="true"
      :disabled="!isEditable"
      placeholder="Search time..."
      :class="['time-select', { isEdited }]"
      @update:model-value="onSelected"
      @search="onSearch"
    >
      <template #open-indicator="{ attributes }">
        <span v-bind="attributes"><ChevronDownIcon /></span>
      </template>
    </VSelect>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, toRefs, watch } from "vue";
import VSelect from "vue-select";

import type { Daytime } from "@ctrl/controller-rpc";

import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";

import { formatDaytimeToString, generateTimeOptions } from "./timeOptions";

export default defineComponent({
  components: {
    ChevronDownIcon,
    VSelect,
  },
  props: {
    isEditable: { type: Boolean, required: true },
    isEdited: { type: Boolean, required: true },
    is24hFormat: { type: Boolean, required: true },
    currentTime: {
      type: Object as PropType<{ hours: number; minutes: number }>,
      required: false,
      default: undefined,
    },
  },
  emits: {
    "update:time": (_time: Daytime) => true,
  },
  setup(props, { emit }) {
    const { is24hFormat } = toRefs(props);
    const selectEl = ref();
    const selectedTime = ref<string>("");
    const searchText = ref<string>("");

    // Generate all time options as simple strings based on format preference
    const timeOptions = computed(() =>
      generateTimeOptions(is24hFormat.value).map((opt) => opt.value)
    );
    const displayOptions = ref<string[]>([]);

    // Update display options when format changes
    watch(
      timeOptions,
      (newOptions) => {
        displayOptions.value = [...newOptions];
      },
      { immediate: true }
    );

    // Watch for currentTime changes and update selectedTime
    watch(
      [() => props.currentTime, is24hFormat],
      ([newTime]) => {
        if (newTime) {
          selectedTime.value = formatDaytimeToString(
            newTime.hours,
            newTime.minutes,
            is24hFormat.value
          );
        } else {
          selectedTime.value = "";
        }
      },
      { immediate: true }
    );

    const validateAndParseTime = (
      value: string
    ): { hours: number; minutes: number } | null => {
      // Try to parse formats like "14:03", "14:3", "1403", "143", "2:30 PM", "2:30PM"
      let hours: number;
      let minutes: number;

      // Check for AM/PM format first
      const ampmMatch = value
        .toUpperCase()
        .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
      if (ampmMatch) {
        hours = parseInt(ampmMatch[1], 10);
        minutes = parseInt(ampmMatch[2], 10);
        const period = ampmMatch[3];

        // Convert 12h to 24h
        if (period === "PM" && hours !== 12) {
          hours += 12;
        } else if (period === "AM" && hours === 12) {
          hours = 0;
        }
      } else if (value.includes(":")) {
        // Format: HH:MM or H:MM or H:M (24h)
        const [hoursStr, minutesStr] = value.split(":");
        hours = parseInt(hoursStr, 10);
        minutes = parseInt(minutesStr, 10);
      } else {
        // Format without colon: "1403" -> 14:03, "143" -> 1:43
        if (value.length === 4) {
          hours = parseInt(value.substring(0, 2), 10);
          minutes = parseInt(value.substring(2), 10);
        } else if (value.length === 3) {
          hours = parseInt(value.substring(0, 1), 10);
          minutes = parseInt(value.substring(1), 10);
        } else {
          return null;
        }
      }

      // Validate ranges
      if (
        isNaN(hours) ||
        isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return null;
      }

      return { hours, minutes };
    };

    const onSelected = (value: string) => {
      if (value) {
        const parsed = validateAndParseTime(value);
        if (parsed) {
          emit("update:time", parsed);
        }
      }
    };

    const onSearch = (search: string) => {
      searchText.value = search;

      // Start with base time options
      const options = [...timeOptions.value];

      // If the search text is a valid time, add the formatted version to options
      if (search) {
        const parsed = validateAndParseTime(search);
        if (parsed) {
          const formatted = formatDaytimeToString(
            parsed.hours,
            parsed.minutes,
            is24hFormat.value
          );
          // Add the formatted time if it's not already in the list
          if (!options.includes(formatted)) {
            options.unshift(formatted);
          }
        }
      }

      displayOptions.value = options;
    };

    return {
      displayOptions,
      onSearch,
      onSelected,
      selectedTime,
      selectEl,
      timeOptions,
    };
  },
});
</script>

<style lang="scss" scoped>
.time-select.v-select {
  @extend %font-caption-regular;
  min-width: 0;
  width: 56px;
}

:deep(.v-select.time-select) {
  .vs__dropdown-toggle {
    border: none;
    box-shadow: none;
  }

  .vs__open-indicator,
  .vs__actions {
    display: none;
  }

  .vs__dropdown-menu {
    min-width: 90px;
  }

  .vs__search {
    @extend %font-caption-regular;
  }

  .vs__selected-options {
    min-width: 42px;
  }

  .vs__selected {
    border-radius: $border-radius-m;
    cursor: text;
    @include transition(background-color 100ms ease-out);
  }
}

.time-select:not(.vs--open) {
  :deep(.vs__selected) {
    @include themed() {
      color: t($grey-70);
    }
  }

  :deep(.vs__selected:hover) {
    @include themed() {
      background-color: t($grey-10);
      color: t($primary);
    }
  }
}

.time-select.vs--open {
  :deep(.vs__selected) {
    @include themed() {
      background-color: t($grey-15);
    }
  }
}
</style>
